import React, { useEffect, useState } from "react"
import { TAfindCampaignById } from "../../../services/campaignsAPI"
import { useNavigate, useParams } from "react-router-dom"
import { Campaign } from "../../../types/CampaignDatas"
import { CampaignProfile } from "../../../components/CampaignProfile"
import AddApplicationModal from "../../../components/AddApplicationModal"
import DetailedApplicationsTable from "../../../components/DetailedApplicationsTable"
import AddApplicationFromExcelModal from "../../../components/AddApplicationFromExcelModal"
import ExcelUploadsModal from "../../../components/ExcelUploadsModal"
import * as XLSX from "xlsx"

export const applicationStates = [
  "invite_new",
  "invite_success",
  "ugc_content",
  "ugc_rejected",
  "first_application",
  "waiting_address",
  "address_to_approve",
  "waiting_content",
  "content_to_share",
  "content_approved",
  "content_shared",
  "application_done",
  "content_rejected",
  "address_rejected",
  "account_rejected",
  "brand_canceled",
  "user_canceled",
  "saved_users",
  "product_sent",
]
export const applicationStateLabels = {
  invite_new: "Davet Edilen Profiller",
  invite_success: "Daveti Kabul Eden Profiller",
  ugc_content: "UGC İçerikler",
  ugc_rejected: "UGC İçerik Reddedildi",
  first_application: "Başvuran Profiller",
  waiting_address: "Adres Beklenen Profiller",
  address_to_approve: "Adres Onayı Bekleniyor",
  waiting_content: "İçerik Bekleniyor",
  content_to_share: "Paylaşılacak İçerik",
  content_approved: "Onaylanan İçerikler",
  content_shared: "Paylaşılan İçerikler",
  application_done: "Başvuru Tamamlandı",
  content_rejected: "İçerik Reddedildi",
  address_rejected: "Adres Reddedildi",
  account_rejected: "Hesap Reddedildi",
  brand_canceled: "Marka Tarafından İptal Edildi",
  user_canceled: "Kullanıcı Tarafından İptal Edildi",
  application_timeout: "Başvuru Zaman Aşımı",
  saved_users: "Kaydedilen Kullanıcılar",
  product_sent: "Ürün Gönderildi",
} as const

export type ApplicationState = keyof typeof applicationStateLabels

const FindCampaign = () => {
  const navigate = useNavigate()
  const { _id } = useParams<{ _id: string }>()
  const [error, setError] = useState<string | null>(null)
  const [campaign, setCampaign] = useState<Campaign>()
  const [loading, setLoading] = useState<boolean>(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false)
  const [isExcelUploadsModalOpen, setIsExcelUploadsModalOpen] = useState(false)
  const [selectedState, setSelectedState] = useState<ApplicationState>("first_application")

  const applicationStates: ApplicationState[] = Object.keys(
    applicationStateLabels,
  ) as ApplicationState[]

  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)
  const handleOpenExcelModal = () => setIsExcelModalOpen(true)
  const handleCloseExcelModal = () => setIsExcelModalOpen(false)
  const handleOpenExcelUploadsModal = () => setIsExcelUploadsModalOpen(true)
  const handleCloseExcelUploadsModal = () => setIsExcelUploadsModalOpen(false)

  const handleUploadComplete = () => {
    // Excel yükleme tamamlandığında başvuruları yenile
    // Bu artık DetailedApplicationsTable içinde handle edilecek
  }

  useEffect(() => {
    if (_id) {
      const handleFindCampaignById = async () => {
        setLoading(true)
        try {
          const campaign_data = await TAfindCampaignById(_id)
          setCampaign(campaign_data)
          setLoading(false)
        } catch (error: unknown) {
          if (
            error &&
            typeof error === "object" &&
            "status" in error &&
            (error as { status: number }).status === 401
          ) {
            navigate("/login")
          }
          setError(error instanceof Error ? error.message : "An error occurred")
          setLoading(false)
        }
      }
      handleFindCampaignById()
    }
  }, [_id, navigate])

  const downloadTemplate = () => {
    const headers = [
      "instagram kullanıcı adı",
      "tiktok kullanıcı adı",
      "yaş",
      "cinsiyet",
      "onay durumu",
      "kullanıcı tutarı",
      "marka tutarı",
      "okul türü(opsiyonel)",
      "okul adı(opsiyonel)",
      "şehir(opsiyonel)",
      "ülke(opsiyonel)",
      "dil(opsiyonel)",
      "para birimi(opsiyonel)",
      "meslek(opsiyonel)",
      "doğum tarihi(opsiyonel)",
    ]
    const exampleRow = [
      "example_user",
      "example_tiktok",
      25,
      "Erkek",
      true,
      100,
      200,
      "Üniversite",
      "Örnek Üniversite",
      "İstanbul",
      "Türkiye",
      "TR",
      "TRY",
      "Öğrenci",
      "1998-01-01",
    ]
    const data = [headers, exampleRow]
    const ws = XLSX.utils.aoa_to_sheet(data)

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Template")
    XLSX.writeFile(wb, "application_template.xlsx")
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
      </div>
    )
  } else {
    return (
      <div className="flex flex-col lg:flex-col justify-between items-start min-h-screen bg-cover bg-center relative">
        <div className="w-full ">{campaign && <CampaignProfile {...campaign} />}</div>
        <div className="w-full">
          <div className="flex flex-col gap-3 py-4">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <button
                className="inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-800 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1"
                onClick={handleOpenModal}
              >
                Yeni Başvuru Oluştur
              </button>
              <button
                className="inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-1"
                onClick={handleOpenExcelModal}
              >
                Yeni Başvuruları Excelden Yükle
              </button>
              <button
                className="inline-flex items-center justify-center rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-800 shadow-sm transition hover:border-violet-300 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-1"
                onClick={handleOpenExcelUploadsModal}
              >
                Excel Yüklemelerini Görüntüle
              </button>
              <button
                className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800 shadow-sm transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1"
                onClick={downloadTemplate}
              >
                Excel Şablonunu İndir
              </button>
            </div>
          </div>
          <AddApplicationModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            _id={_id || ""}
            currency={campaign?.currency || "TRY"}
          />
          <AddApplicationFromExcelModal
            isOpen={isExcelModalOpen}
            onClose={handleCloseExcelModal}
            _id={_id || ""}
            platform={
              campaign?.platform.includes("insta")
                ? "instagram"
                : campaign?.platform.includes("tiktok")
                ? "tiktok"
                : "ugc"
            }
            onUploadComplete={handleUploadComplete}
          />
          <ExcelUploadsModal
            isOpen={isExcelUploadsModalOpen}
            onClose={handleCloseExcelUploadsModal}
            campaignId={_id || ""}
            onUploadComplete={handleUploadComplete}
          />

          <label className="text-lg mt-5 font-semibold text-gray-800">Başvurular</label>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {applicationStates.map((state: ApplicationState) => {
              const isActive = selectedState === state
              return (
                <button
                  key={state}
                  onClick={() => setSelectedState(state)}
                  className={`flex w-full items-center justify-center rounded-lg border text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    isActive
                      ? "border-indigo-500 bg-indigo-600 text-white shadow-sm focus-visible:ring-indigo-400"
                      : "border-gray-200 bg-white text-gray-700 hover:border-indigo-200 hover:bg-indigo-50 focus-visible:ring-indigo-300"
                  }`}
                  style={{ minHeight: "48px" }}
                >
                  {applicationStateLabels[state] || state}
                </button>
              )
            })}
          </div>
          <DetailedApplicationsTable
            campaignId={_id || ""}
            selectedState={selectedState}
            barter={campaign?.barter}
          />
        </div>

        <form className="w-1/4 absolute top-5 right-6">
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <div className="flex justify-center">
            <div className="relative inline-flex"></div>
          </div>
        </form>
      </div>
    )
  }
}

export default FindCampaign
